import React from 'react';
import { datadogLogs } from '@datadog/browser-logs';
// @ts-ignore
import { isVIP } from 'lib/telemetry';
import { Recipient } from 'types/typescript/messagingTypes';
import { connect, Dispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { saveDidSearchRecipients } from 'reducers/roleBasedMessagingSearch';

type Props = {
  orgsLoading: boolean;
  groupsLoading: boolean;
  groupChatsLoading: boolean;
  individualRecipientsLoading: boolean;
  selection: Recipient[];
  page: number;
} & ReduxDispatchProps;

type ReduxDispatchProps = {
  saveDidSearchRecipients?: (x: boolean) => void;
};

type State = {
  startTime?: Date;
  orgsLoadedAt?: Date;
  groupsLoadedAt?: Date;
  groupChatsLoadedAt?: Date;
  individualRecipientsLoadedAt?: Date;
  searchCountForSelection: number;
  didSearch: boolean;
};

class SearchTimer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Set startTime for initial request where GQL requests for orgs & groups have already been made
    this.state = {
      startTime: props.orgsLoading && props.groupsLoading ? new Date() : undefined,
      searchCountForSelection: 0,
      didSearch: false,
    };
  }

  getLoadTime(finishTime?: Date) {
    const { startTime } = this.state;
    if (finishTime && startTime) {
      return finishTime.getTime() - startTime.getTime();
    } else {
      return 0;
    }
  }

  getLongestLoadTime() {
    return Math.max(
      this.getLoadTime(this.state.orgsLoadedAt),
      this.getLoadTime(this.state.groupsLoadedAt),
      this.getLoadTime(this.state.groupChatsLoadedAt),
      this.getLoadTime(this.state.individualRecipientsLoadedAt)
    );
  }

  isAnyLoading(props: Props) {
    return (
      props.orgsLoading ||
      props.groupsLoading ||
      props.groupChatsLoading ||
      props.individualRecipientsLoading
    );
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const wasLoading = this.isAnyLoading(prevProps);
    const isLoading = this.isAnyLoading(this.props);

    const newState = { ...this.state };
    let stateChanged = false;

    if (!wasLoading && isLoading && !this.state.startTime) {
      newState.startTime = new Date();
      stateChanged = true;
    }

    if (prevProps.orgsLoading && !this.props.orgsLoading) {
      newState.orgsLoadedAt = new Date();
      stateChanged = true;
    }
    if (prevProps.groupsLoading && !this.props.groupsLoading) {
      newState.groupsLoadedAt = new Date();
      stateChanged = true;
    }
    if (prevProps.groupChatsLoading && !this.props.groupChatsLoading) {
      newState.groupChatsLoadedAt = new Date();
      stateChanged = true;
    }
    if (prevProps.individualRecipientsLoading && !this.props.individualRecipientsLoading) {
      newState.individualRecipientsLoadedAt = new Date();
      stateChanged = true;
    }

    // Track how many searches are made before a selection
    if (prevProps.selection !== this.props.selection && this.state.searchCountForSelection > 0) {
      newState.searchCountForSelection = 0;
      stateChanged = true;

      datadogLogs.logger.info('search.sla.2020.search.character_count.web', {
        count: this.state.searchCountForSelection,
        is_vip: isVIP(),
      });
    }

    // Initial request
    // We're not checking for individualRecipientsLoading here because it's almost always undefined.
    // Somewhere else in the site has probably already got it loaded before we get to the composer.
    if (this.state.orgsLoadedAt && this.state.groupsLoadedAt && this.state.groupChatsLoadedAt) {
      datadogLogs.logger.info('search.sla.2020.empty.search.request.time.web', {
        time_ms: this.getLongestLoadTime(),
        is_vip: isVIP(),
      });

      this.setState({
        startTime: undefined,
        orgsLoadedAt: undefined,
        groupsLoadedAt: undefined,
        groupChatsLoadedAt: undefined,
        individualRecipientsLoadedAt: undefined,
        searchCountForSelection: 0,
        didSearch: this.state.didSearch,
      });
      // Subsequent searches
    } else if (
      !this.state.orgsLoadedAt &&
      !this.state.groupsLoadedAt &&
      this.state.groupChatsLoadedAt &&
      this.state.individualRecipientsLoadedAt
    ) {
      datadogLogs.logger.info('search.sla.2020.some.search.request.time.web', {
        time_ms: this.getLongestLoadTime(),
        is_vip: isVIP(),
      });

      this.setState({
        startTime: undefined,
        orgsLoadedAt: undefined,
        groupsLoadedAt: undefined,
        groupChatsLoadedAt: undefined,
        individualRecipientsLoadedAt: undefined,
        searchCountForSelection: this.state.searchCountForSelection + 1,
        didSearch: true,
      });
    } else if (stateChanged) {
      this.setState(newState);
    }
  }

  componentWillUnmount() {
    this.props.saveDidSearchRecipients?.(this.state.didSearch);
  }

  render() {
    return null;
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): any =>
  bindActionCreators({ saveDidSearchRecipients }, dispatch);

export default connect(undefined, mapDispatchToProps)(SearchTimer);
